/**
 * LocaleProvider (Web)
 *
 * React context provider for locale management in web applications.
 * Wraps the app with I18nextProvider and provides locale state management.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { I18nextProvider } from 'react-i18next';

import { changeLocaleWeb, i18n, initLocaleWeb } from '../config';
import {
  DEFAULT_LOCALE,
  type LocaleConfig,
  type LocaleContextType,
  type SupportedLocale,
} from '../types';

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════════════════

const LocaleContext = createContext<LocaleContextType | null>(null);

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER PROPS
// ═══════════════════════════════════════════════════════════════════════════

export interface LocaleProviderProps extends LocaleConfig {
  /** Child components */
  children: ReactNode;
}

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * LocaleProvider component for web applications
 *
 * @example
 * ```tsx
 * <LocaleProvider detectLanguage>
 *   <App />
 * </LocaleProvider>
 * ```
 */
export function LocaleProvider({
  children,
  initialLocale,
  detectLanguage = true,
  debug = false,
}: LocaleProviderProps) {
  // i18n is pre-initialized with resources in shared.ts, so translations are available immediately
  const [locale, setLocaleState] = useState<SupportedLocale>(
    (i18n.language as SupportedLocale) || initialLocale || DEFAULT_LOCALE
  );
  const [isLoading, setIsLoading] = useState(false);
  const isInitialized = true; // Already initialized in shared.ts

  // Configure locale detection and persistence on mount
  useEffect(() => {
    let mounted = true;

    async function configure() {
      try {
        await initLocaleWeb({ initialLocale, detectLanguage, debug });

        if (mounted) {
          // Sync state with i18n after locale detection
          setLocaleState(i18n.language as SupportedLocale);
        }
      } catch (error) {
        console.error('[LocaleProvider] Failed to configure locale:', error);
      }
    }

    configure();

    return () => {
      mounted = false;
    };
  }, [initialLocale, detectLanguage, debug]);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setLocaleState(lng as SupportedLocale);
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  // Change locale handler
  const setLocale = useCallback(async (newLocale: SupportedLocale) => {
    setIsLoading(true);
    try {
      await changeLocaleWeb(newLocale);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Context value
  const value = useMemo<LocaleContextType>(
    () => ({
      locale,
      isLoading,
      isInitialized,
      setLocale,
    }),
    [locale, isLoading, isInitialized, setLocale]
  );

  return (
    <LocaleContext.Provider value={value}>
      <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
    </LocaleContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Hook to access locale context
 *
 * @example
 * ```tsx
 * const { locale, setLocale, isLoading } = useLocaleContext();
 * ```
 */
export function useLocaleContext(): LocaleContextType {
  const context = useContext(LocaleContext);

  if (!context) {
    throw new Error('useLocaleContext must be used within a LocaleProvider');
  }

  return context;
}

export { LocaleContext };
